import {
  useState,
  useEffect,
  useCallback,
  SyntheticEvent,
  useRef,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Drawer,
  Divider,
  TablePagination,
  Tabs,
  Tab,
  Chip,
  IconButton,
} from "@mui/material";
// Icons
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  useNodesState,
  useEdgesState,
  MarkerType,
  Node,
  Edge,
} from "reactflow";

import {
  getWorkflow,
  listWorkflowRuns,
  getWorkflowRunDetails,
  resumeWorkflowRun,
  getWorkflowWebhookConfig,
} from "../../api/agentApi";
import WorkflowNode from "./WorkflowNode.tsx";
import {
  getLayoutedElements,
  loadWorkflowNodesWithTriggers,
} from "./workflowUtils.ts";
import { getScheduleLabel } from "./CronScheduleBuilder.tsx";
// @ts-ignore
import GeneralTabPanel from "../AgentDetail/GeneralTabPanel.jsx";
import DetailItem from "../AgentDetail/DetailItem.tsx";
import WorkflowRunDetailsContent from "./WorkflowRunDetailsContent.tsx";
import { useFeatureFlags } from "../../context/FeatureFlagContext.tsx";
import {
  Workflow,
  WorkflowRun,
  WorkflowRunDetails,
} from "./WorkflowTypes.ts";

const nodeTypes = { muiNode: WorkflowNode };

export default function WorkflowDetailPage() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const { isEnabled } = useFeatureFlags();
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [runsLoading, setRunsLoading] = useState<boolean>(false);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [totalRuns, setTotalRuns] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedRun, setSelectedRun] = useState<WorkflowRun | null>(null);
  const [runDetails, setRunDetails] = useState<WorkflowRunDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const runDetailsSignatureRef = useRef<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [runsRefreshToken, setRunsRefreshToken] = useState<number>(0);

  // Schedule config state
  const [scheduleConfig, setScheduleConfig] = useState<any>(null);

  // Webhook config state
  const [webhookConfig, setWebhookConfig] = useState<any>(null);

  const [nodes, setNodes] = useNodesState<Node>([]);
  const [edges, setEdges] = useEdgesState<Edge>([]);

  // Save viz tab node positions to localStorage on drag stop
  // Key format: wf-{id}-{version} (shared with edit page so layout carries over)

  const runNodes = useMemo(() => {
    if (!runDetails) {
      return nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          show_status: false,
          status: undefined,
        },
      }));
    }

    const stepsLog =
      (runDetails.execution_logs as any)?.steps &&
      typeof (runDetails.execution_logs as any).steps === "object"
        ? (runDetails.execution_logs as any).steps
        : {};
    const stepResults =
      runDetails.step_results && typeof runDetails.step_results === "object"
        ? runDetails.step_results
        : {};

    const normalizeStatus = (status?: string | null) => {
      if (status === "success") return "completed";
      if (status === "completed") return "completed";
      if (status === "failed" || status === "error") return "failed";
      if (status === "running") return "running";
      if (status === "queued") return "ready";
      if (status === "paused" || status === "waiting_for_input") return "paused";
      if (status === "skipped") return "skipped";
      return "ready";
    };

    // When workflow is in a terminal state, convert leftover "queued" to "skipped"
    const workflowStatus = runDetails.status;
    const isTerminal = workflowStatus === "completed" || workflowStatus === "failed";

    return nodes.map((node) => {
      if (node.data.type === "trigger") {
        return {
          ...node,
          data: {
            ...node.data,
            show_status: false,
            status: undefined,
          },
        };
      }

      const logStatus = stepsLog?.[node.id]?.status;
      const resultStatus = stepResults?.[node.id]?.status;
      // step_results is more authoritative (updated after step completes),
      // execution_logs may lag (e.g. human_input shows "running" even after approval)
      let resolvedStatus = normalizeStatus(resultStatus || logStatus);

      // Steps that were never executed should show as "skipped", not "ready"
      if (isTerminal && resolvedStatus === "ready") {
        resolvedStatus = "skipped";
      }

      return {
        ...node,
        data: {
          ...node.data,
          show_status: true,
          status: resolvedStatus,
        },
      };
    });
  }, [nodes, runDetails]);

  useEffect(() => {
    const fetchWorkflow = async (): Promise<void> => {
      if (!workflowId) return;

      try {
        const data: Workflow = await getWorkflow(workflowId);
        setWorkflow(data);

        // Store schedule config from workflow response
        if (data.schedule_config) {
          setScheduleConfig(data.schedule_config);
        }

        // Fetch webhook config
        try {
          const webhookCfg = await getWorkflowWebhookConfig(workflowId);
          if (webhookCfg && webhookCfg.adapter_type) {
            setWebhookConfig(webhookCfg);
          }
        } catch (err) {
          // Silently fail - not all workflows have webhooks
        }

        // Load workflow nodes and edges with triggers (schedule and webhook)
        // Triggers are loaded from workflow.schedule_config and workflow.webhook_config
        const { nodes: initialNodes, edges: initialEdges } =
          await loadWorkflowNodesWithTriggers(data, "default", {
            type: MarkerType.ArrowClosed,
          }, false);

        // Apply layout
        const { nodes: layoutedNodes, edges: layoutedEdges } =
          getLayoutedElements(initialNodes, initialEdges);

        // Restore saved positions from localStorage if available
        // Key format: wf-{id}-{version} (shared with edit page)
        const savedKey = `wf-${workflowId}-${data.version ?? 1}`;
        const savedLayout = localStorage.getItem(savedKey);
        let finalNodes = layoutedNodes;
        if (savedLayout) {
          try {
            const parsed = JSON.parse(savedLayout);
            finalNodes = layoutedNodes.map((node) => {
              const saved = parsed[node.id];
              if (saved && typeof saved === 'object' && 'x' in saved && 'y' in saved) {
                return { ...node, position: saved };
              }
              return node;
            });
          } catch { /* ignore corrupt data */ }
        }

        setNodes(finalNodes);
        setEdges(layoutedEdges);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkflow();
  }, [workflowId, setNodes, setEdges]);

  // Utility function to sort workflow runs by started_at time (latest first)
  const sortRunsByTime = (runs: any[]): any[] => {
    return [...runs].sort((a: any, b: any) => {
      const aTime = new Date(a.started_at ?? a.created_at ?? 0).getTime();
      const bTime = new Date(b.started_at ?? b.created_at ?? 0).getTime();
      return bTime - aTime;
    });
  };

  const fetchRuns = useCallback(async (): Promise<void> => {
    if (!workflowId) return;

    setRunsLoading(true);
    setRunsError(null);
    try {
      const data = await listWorkflowRuns(workflowId, {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      if (data && Array.isArray((data as any).runs)) {
        setRuns(sortRunsByTime((data as any).runs));
        setTotalRuns((data as any).total || 0);
      } else if (Array.isArray(data)) {
        // Fallback for older API format
        setRuns(sortRunsByTime(data));
        setTotalRuns(data.length);
      } else {
        setRuns([]);
        setTotalRuns(0);
      }
    } catch (err) {
      setRunsError((err as Error).message);
    } finally {
      setRunsLoading(false);
    }
  }, [workflowId, page, rowsPerPage]);

  const fetchRunDetails = useCallback(
    async (
      version: number,
      runNumber: number | string,
      options?: { silent?: boolean }
    ): Promise<WorkflowRunDetails | null> => {
      if (!workflowId) return null;
      const shouldShowLoading = !options?.silent;
      if (shouldShowLoading) {
        setDetailsLoading(true);
        setDetailsError(null);
      }
      try {
        const data: WorkflowRunDetails = await getWorkflowRunDetails(
          workflowId,
          version,
          runNumber
        );
        const signature = JSON.stringify({
          status: data.status,
          started_at: data.started_at,
          completed_at: data.completed_at,
          final_output: data.final_output,
          error_message: data.error_message,
          step_results: data.step_results,
          execution_logs: data.execution_logs,
        });

        // Always update state; skip only during silent polling when nothing changed
        if (!options?.silent || runDetailsSignatureRef.current !== signature) {
          runDetailsSignatureRef.current = signature;
          setRunDetails(data);
        }
        return data;
      } catch (err) {
        if (shouldShowLoading) {
          setDetailsError((err as Error).message);
        }
        return null;
      } finally {
        if (shouldShowLoading) {
          setDetailsLoading(false);
        }
      }
    },
    [workflowId]
  );

  useEffect(() => {
    if (!drawerOpen || !selectedRun) return undefined;
    const runId = selectedRun.run_number ?? selectedRun.runId ?? selectedRun.id;
    const version = selectedRun.workflow_version ?? selectedRun.version ?? 1;
    if (runId == null || version == null) return undefined;

    let isActive = true;
    let intervalId: number | undefined;

    const isTerminal = (status?: string | null) =>
      status === "completed" ||
      status === "failed" ||
      status === "cancelled" ||
      status === "error";

    const startPolling = async () => {
      const initialData = await fetchRunDetails(version, runId);
      if (!isActive) return;
      if (isTerminal(initialData?.status)) return;

      intervalId = window.setInterval(async () => {
        const nextData = await fetchRunDetails(version, runId, { silent: true });
        if (!isActive) return;
        if (isTerminal(nextData?.status)) {
          if (intervalId !== undefined) {
            window.clearInterval(intervalId);
            intervalId = undefined;
          }
        }
      }, 3000);
    };

    startPolling();

    return () => {
      isActive = false;
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [drawerOpen, selectedRun, fetchRunDetails]);

  // Fetch runs when Run History tab is opened or when runsRefreshToken changes
  useEffect(() => {
    if (tabValue === 2) {
      fetchRuns();
    }
  }, [tabValue, fetchRuns, runsRefreshToken]);

  // Poll for updates when there are active runs (queued or running)
  useEffect(() => {
    if (!isEnabled("enablePolling") || !workflowId || tabValue !== 2) return;

    const hasActiveRuns = runs?.some(
      (run: WorkflowRun) => {
        const s = run.status?.toLowerCase();
        return s === "running" || s === "queued" || s === "paused" || s === "waiting_for_input";
      }
    );
    if (!hasActiveRuns) return;

    const pollIntervalMs = 2000;
    const pollInterval = setInterval(async () => {
      try {
        const data = await listWorkflowRuns(workflowId, {
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        });
        if (data && Array.isArray((data as any).runs)) {
          setRuns(sortRunsByTime((data as any).runs));
          setTotalRuns((data as any).total || 0);
        } else if (Array.isArray(data)) {
          setRuns(sortRunsByTime(data));
          setTotalRuns(data.length);
        }
      } catch (err) {
        console.error("Failed to poll workflow runs:", err);
      }
    }, pollIntervalMs);

    return () => clearInterval(pollInterval);
  }, [workflowId, runs, page, rowsPerPage, tabValue, isEnabled]);

  // Suppress ResizeObserver warnings (common with React Flow)
  useEffect(() => {
    const resizeObserverErrHandler = (e: ErrorEvent) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", resizeObserverErrHandler);
    return () => window.removeEventListener("error", resizeObserverErrHandler);
  }, []);

  const handleRowClick = (run: WorkflowRun): void => {
    // Clear signature so the upcoming fetch always renders, even for the same run
    runDetailsSignatureRef.current = null;
    setSelectedRun(run);
    setRunDetails(null);
    setDrawerOpen(true);
    setActiveTabIndex(0);
    // fetchRunDetails is triggered by the useEffect watching [drawerOpen, selectedRun]
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedRun(null);
  };

  function a11yProps(index: number) {
    return {
      id: `workflow-tab-${index}`,
      "aria-controls": `workflow-tabpanel-${index}`,
    };
  }

  if (loading)
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  if (!workflow)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Workflow not found</Alert>
      </Box>
    );

  return (
    <Box sx={{ width: "100%" }}>
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0,
          p: 3,
          pb: 0,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            {workflow.name}{" "}
            <Typography
              component="span"
              variant="h5"
              color="text.secondary"
              sx={{ fontWeight: 300 }}
            >
              (v{workflow.version})
            </Typography>
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(`/workflow/${workflowId}/edit`)}
            sx={{ borderRadius: "99px", textTransform: "none", px: 3 }}
          >
            Edit Workflow
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/workflow/${workflowId}/run`)}
            sx={{ borderRadius: "99px", textTransform: "none", px: 3 }}
          >
            Run Workflow
          </Button>
        </Box>
      </Box>

      {/* TABS */}
      <Box
        sx={{
          width: "100%",
          borderBottom: 1,
          borderColor: "divider",
          px: 3,
          // mt: 2,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="workflow details tabs"
        >
          <Tab
            label="Workflow Details"
            {...a11yProps(0)}
            sx={{ textTransform: "none" }}
          />
          <Tab
            label="Visualization"
            {...a11yProps(1)}
            sx={{ textTransform: "none" }}
          />
          <Tab
            label="Run History"
            {...a11yProps(2)}
            sx={{ textTransform: "none" }}
          />
        </Tabs>
      </Box>

      {/* TAB 0: DETAILS */}
      <GeneralTabPanel value={tabValue} index={0} sx={{ p: 3 }}>
        <Paper elevation={0} sx={{ p: 2 }}>
          <DetailItem
            label="Workflow Name"
            value={workflow.dag_definition.workflow_name || workflow.name}
          />
          <DetailItem label="Workflow ID" value={workflow.id} />
          <DetailItem
            label="Description"
            value={workflow.dag_definition.description || "-"}
          />
          <DetailItem
            label="Total Steps"
            value={workflow.dag_definition.steps?.length || 0}
          />
          <DetailItem
            label="Global Timeout"
            value={`${
              workflow.dag_definition.global_timeout_seconds || 1800
            } seconds`}
          />

          {/* Schedule Trigger Details */}
          {scheduleConfig && scheduleConfig.enabled && (
            <>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ my: 1.5, color: "#334155" }}
              >
                Schedule Trigger
              </Typography>
              <DetailItem
                label="Schedule Type"
                value={
                  <Chip
                    label={getScheduleLabel(scheduleConfig.cron_expression)}
                    sx={{
                      bgcolor: "#94A3B8",
                      color: "#fff",
                      fontWeight: 500,
                      borderRadius: "9999px",
                      height: 24,
                      px: 1.5,
                    }}
                  />
                }
              />
              <DetailItem
                label="Cron Expression"
                value={scheduleConfig.cron_expression}
              />
              <DetailItem
                label="Status"
                value={
                  <Chip
                    label={scheduleConfig.enabled ? "Enabled" : "Disabled"}
                    sx={{
                      bgcolor: scheduleConfig.enabled ? "#43a047" : "#e0e0e0",
                      color: scheduleConfig.enabled ? "#fff" : "#333",
                      fontWeight: 500,
                      borderRadius: "9999px",
                      height: 24,
                      px: 1.5,
                    }}
                  />
                }
              />
              {scheduleConfig.last_run_at && (
                <DetailItem
                  label="Last Run"
                  value={new Date(scheduleConfig.last_run_at).toLocaleString()}
                />
              )}
            </>
          )}

          {/* Webhook Trigger Details */}
          {webhookConfig && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "#F8FAFC", borderRadius: "8px", border: "1px solid #E2E8F0" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Webhook Trigger
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Chip label={webhookConfig.adapter_type} size="small" sx={{ fontWeight: 600 }} />
                <Chip
                  label={webhookConfig.enabled ? "Active" : "Inactive"}
                  size="small"
                  color={webhookConfig.enabled ? "success" : "default"}
                />
              </Box>
              {webhookConfig.secrets_ref && Object.entries(webhookConfig.secrets_ref).map(([name, ref]: [string, any]) => (
                <Typography key={name} variant="body2" sx={{ fontSize: "13px", color: "#64748B" }}>
                  {name}: {typeof ref === "object" && ref?.key_name ? ref.key_name : "(configured)"}
                </Typography>
              ))}
              <Typography variant="caption" sx={{ color: "#94A3B8", mt: 1, display: "block" }}>
                Endpoint: POST /workflow/{workflowId}/webhook
              </Typography>
            </Box>
          )}

          <Accordion
            defaultExpanded={false}
            sx={{
              mb: 3,
              boxShadow: "none",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight="bold">
                Workflow Definition (JSON)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <pre
                style={{
                  backgroundColor: "#f8fafc",
                  padding: "16px",
                  borderRadius: "8px",
                  overflowX: "auto",
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  fontSize: "0.85rem",
                  margin: 0,
                  border: "1px solid #f1f5f9",
                }}
              >
                {JSON.stringify(workflow, null, 2)}
              </pre>
            </AccordionDetails>
          </Accordion>
        </Paper>
      </GeneralTabPanel>

      {/* TAB 1: VISUALIZATION */}
      <GeneralTabPanel
        value={tabValue}
        index={1}
        sx={{ p: 3, height: "calc(100vh - 220px)" }}
      >
        {/* <Paper
          sx={{
            height: "100%",
            width: "100%",
            border: "1px solid #e0e0e0",
            position: "relative",
          }}
          elevation={0}
        >
          <ReactFlow
            nodes={nodes}
            edges={vizEdges}
            onNodesChange={onNodesChange as OnNodesChange}
            onEdgesChange={onEdgesChange as OnEdgesChange}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onNodeDragStop={handleVizNodeDragStop}
            nodeTypes={nodeTypes}
            defaultViewport={{ x: 0, y: 0, zoom: 0.9 }}
            fitView
            fitViewOptions={{
              padding: 0.2,
              minZoom: 0.6,
              maxZoom: 1.0,
            }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            deleteKeyCode={null}
            style={{ width: "100%", height: "100%" }}
          >
            <Controls />
            <Background color="#f8f9fa" gap={16} />
            <Panel position="top-left">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showDataMappings}
                      onChange={(e) => setShowDataMappings(e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '11px', color: '#64748B', userSelect: 'none' }}>
                      Show data mappings
                    </Typography>
                  }
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.9)',
                    borderRadius: '8px',
                    px: 1,
                    py: 0.25,
                    border: '1px solid #e2e8f0',
                    m: 0,
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const { nodes: ln, edges: le } = getLayoutedElements(nodes, edges);
                    setNodes(ln);
                    setEdges(le);
                  }}
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#64748B',
                    borderColor: '#e2e8f0',
                    bgcolor: 'rgba(255,255,255,0.9)',
                    borderRadius: '8px',
                    px: 1.5,
                    py: 0.25,
                    minWidth: 0,
                    textTransform: 'none',
                    '&:hover': { borderColor: '#bc8e5c', color: '#bc8e5c', bgcolor: 'white' },
                  }}
                >
                  Auto Layout
                </Button>
              </Box>
            </Panel>
          </ReactFlow>

          <WorkflowPropertiesDrawer
            open={nodeDrawerOpen}
            onClose={() => setNodeDrawerOpen(false)}
            selectedElement={selectedElement}
            readonly={true}
            workflowId={workflowId}
          />
        </Paper> */}
        {/* <ThinkingTimelineNew /> */}
      </GeneralTabPanel>

      {/* TAB 2: HISTORY */}
      <GeneralTabPanel value={tabValue} index={2} sx={{ p: 3 }}>
        <Box
          sx={{
            width: "100%",
            mb: 3,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={fetchRuns}
            disabled={runsLoading}
            sx={{ mb: 2 }}
          >
            {runsLoading ? "Refreshing..." : "Refresh"}
          </Button>

          {runsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {runsError}
            </Alert>
          )}
          {runsLoading && runs.length === 0 ? (
            <CircularProgress size={24} />
          ) : runs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No runs yet.
            </Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ bgcolor: "#f5f5f5", fontWeight: 600, py: 0.5 }}
                  >
                    Version
                  </TableCell>
                  <TableCell
                    sx={{ bgcolor: "#f5f5f5", fontWeight: 600, py: 0.5 }}
                  >
                    Run #
                  </TableCell>
                  <TableCell
                    sx={{ bgcolor: "#f5f5f5", fontWeight: 600, py: 0.5 }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{ bgcolor: "#f5f5f5", fontWeight: 600, py: 0.5 }}
                  >
                    Trigger Type
                  </TableCell>
                  <TableCell
                    sx={{ bgcolor: "#f5f5f5", fontWeight: 600, py: 0.5 }}
                  >
                    Started At
                  </TableCell>
                  <TableCell
                    sx={{ bgcolor: "#f5f5f5", fontWeight: 600, py: 0.5 }}
                  >
                    Steps Completed
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ bgcolor: "#f5f5f5", fontWeight: 600, py: 0.5 }}
                  >
                    Details
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((run: WorkflowRun) => (
                  <TableRow
                    key={`${run.workflow_version ?? 0}-${run.run_number ?? run.runId ?? run.id}`}
                    hover
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      {run.workflow_version != null ? `${run.workflow_version}` : "—"}
                    </TableCell>
                    <TableCell>
                      {run.run_number ?? run.runId ?? run.id}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={run.status ?? "unknown"}
                        size="small"
                        color={
                          run.status === "success" || run.status === "completed"
                            ? "success"
                            : run.status === "failed" || run.status === "error"
                            ? "error"
                            : run.status === "paused" || run.status === "waiting_for_input"
                            ? "warning"
                            : run.status === "running"
                            ? "info"
                            : "default"
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{run.trigger_type ?? "manual"}</TableCell>
                    <TableCell>
                      {run.started_at ?? run.created_at ?? "—"}
                    </TableCell>
                    <TableCell>
                      {run.steps_completed ?? "—"}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        {(run.status === "paused" || run.status === "waiting_for_input") && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const version = run.workflow_version ?? workflow?.version ?? 1;
                                const runNum = run.run_number ?? run.runId ?? run.id;
                                await resumeWorkflowRun(workflowId!, version, runNum, undefined);
                                fetchRuns();
                              } catch (err) {
                                alert(`Resume failed: ${(err as Error).message}`);
                              }
                            }}
                            sx={{ textTransform: 'none', fontSize: '11px', minWidth: 'auto', px: 1 }}
                          >
                            Resume
                          </Button>
                        )}
                        <IconButton
                          onClick={() => handleRowClick(run)}
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalRuns}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
            </>
          )}

          {/* RUN DETAILS DRAWER on icon click */}
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            variant="temporary"
            sx={{
              zIndex: 1300,
              "& .MuiDrawer-paper": { width: "72%", boxSizing: "border-box" },
            }}
          >
            <Box
              sx={{
                p: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: "action.hover"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography variant="h6" gutterBottom>
                  Run Details{" "}
                  {selectedRun
                    ? `(Run ${
                        selectedRun.run_number ??
                        selectedRun.runId ??
                        selectedRun.id
                      })`
                    : ""}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseDrawer}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />
            {detailsLoading && <CircularProgress size={24} />}
            {detailsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {detailsError}
              </Alert>
            )}
            {runDetails && !detailsLoading && (
              <WorkflowRunDetailsContent
                workflowRunDetails={runDetails}
                activeTabIndex={activeTabIndex}
                onClose={handleCloseDrawer}
                onTabChange={(_, newValue) => setActiveTabIndex(newValue)}
                nodes={runNodes}
                edges={edges}
                nodeTypes={nodeTypes}
                layoutKey={`wf-run-${workflowId}-${selectedRun?.workflow_version ?? workflow?.version ?? 1}-${selectedRun?.run_number ?? selectedRun?.runId ?? selectedRun?.id ?? 0}`}
              />
            )}
          </Drawer>
        </Box>
      </GeneralTabPanel>

    </Box>
  );
}
